import { AlertCircle } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { FormErrorText } from "../../../../components/form/FormErrorText";
import { Input, Label, SearchSelect, Switch } from "../../../../components";
import { Button } from "../../../../components/button";
import type { PublicOption } from "../../../common/public-options/publicOptions.api";
import {
  getDefaultNotificationTimezone,
  getNotificationTimezoneOptions,
} from "../../utils/notificationTimezones";
import {
  createNotificationSchema,
  type CreateNotificationFormValues,
} from "./createNotification.schema";

const MAX_TITLE_LENGTH = 500;

interface NotificationFormProps {
  formId?: string;
  loading: boolean;
  error: string | null;
  alertTypes: PublicOption[];
  alertTypesLoading: boolean;
  alertTypesError: string | null;
  onRetryAlertTypes?: () => void | Promise<void>;
  defaultValues?: Partial<CreateNotificationFormValues>;
  showAlertTypeSelector?: boolean;
  readOnlyAlertTypeLabel?: string;
  onSubmit: (values: CreateNotificationFormValues) => Promise<void>;
}

function FieldGroup({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-gray-200 p-4 dark:border-[#394150]">
      {title ? (
        <h3 className="mb-4 text-sm font-semibold text-foreground">{title}</h3>
      ) : null}
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export function NotificationForm({
  formId,
  loading,
  error,
  alertTypes,
  alertTypesLoading,
  alertTypesError,
  onRetryAlertTypes,
  defaultValues,
  showAlertTypeSelector = true,
  readOnlyAlertTypeLabel,
  onSubmit,
}: NotificationFormProps) {
  const { t } = useTranslation();
  const timezoneOptions = useMemo(() => getNotificationTimezoneOptions(), []);
  const form = useForm<CreateNotificationFormValues>({
    resolver: zodResolver(createNotificationSchema(t)),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      title: defaultValues?.title ?? "",
      content: defaultValues?.content ?? "",
      alert_type_id: defaultValues?.alert_type_id ?? "",
      timezone: defaultValues?.timezone ?? getDefaultNotificationTimezone(),
      is_pinned: defaultValues?.is_pinned ?? false,
    },
  });

  const {
    control,
    handleSubmit,
    register,
    watch,
    reset,
    formState: { errors },
  } = form;
  const isPinned = watch("is_pinned");
  const titleValue = watch("title") ?? "";
  const contentValue = watch("content") ?? "";

  useEffect(() => {
    if (!defaultValues) {
      return;
    }

    reset({
      title: defaultValues.title ?? "",
      content: defaultValues.content ?? "",
      alert_type_id: defaultValues.alert_type_id ?? "",
      timezone: defaultValues.timezone || getDefaultNotificationTimezone(),
      is_pinned: defaultValues.is_pinned ?? false,
    });
  }, [defaultValues, reset]);

  const handleFormSubmit = handleSubmit(async (values) => {
    await onSubmit(values);
  });

  return (
    <div className="px-6 py-4">
      {error && (
        <div
          className="mb-4 flex items-center gap-2 rounded border border-red-400 bg-red-100 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <form
        id={formId}
        onSubmit={handleFormSubmit}
        className="space-y-6"
      >
        <FieldGroup title={t("notification.center")}>
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <Label htmlFor="title">{t("notification.title")} *</Label>
              <span className="text-xs text-muted-foreground">
                {titleValue.length}/{MAX_TITLE_LENGTH}
              </span>
            </div>
            <Input
              id="title"
              type="text"
              maxLength={MAX_TITLE_LENGTH}
              placeholder={t("notification.enter_notification_title")}
              disabled={loading}
              aria-invalid={errors.title ? "true" : "false"}
              className={`w-full ${errors.title ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
              {...register("title")}
            />
            <FormErrorText
              message={errors.title?.message}
              className="mt-1"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <Label htmlFor="content">{t("notification.content")} *</Label>
              <span className="text-xs text-muted-foreground">{contentValue.length}</span>
            </div>
            <textarea
              id="content"
              placeholder={t("notification.enter_notification_content")}
              disabled={loading}
              rows={5}
              aria-invalid={errors.content ? "true" : "false"}
              className={`min-h-[120px] w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                errors.content ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""
              }`}
              {...register("content")}
            />
            <FormErrorText
              message={errors.content?.message}
              className="mt-1"
            />
          </div>
        </FieldGroup>

        <FieldGroup title={t("notification.alert_type")}>
          {showAlertTypeSelector ? (
            <div className="space-y-2">
              <Label htmlFor="alert_type">{t("notification.alert_type")} *</Label>
              {alertTypesLoading ? (
                <div
                  className="h-10 w-full animate-pulse rounded-md bg-gray-200 dark:bg-gray-700"
                  aria-hidden="true"
                  data-testid="alert-type-skeleton"
                />
              ) : alertTypesError ? (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {alertTypesError}
                  </div>
                  {onRetryAlertTypes ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void onRetryAlertTypes()}
                    >
                      {t("ui.retry")}
                    </Button>
                  ) : null}
                </div>
              ) : (
                <Controller
                  control={control}
                  name="alert_type_id"
                  render={({ field }) => {
                    return (
                      <SearchSelect
                        value={field.value}
                        onChange={field.onChange}
                        options={alertTypes.map((item) => ({
                          ...item,
                          label: `${item.icon} ${item.label}`,
                        }))}
                        placeholder={t("notification.select_alert_type")}
                        searchPlaceholder="Search alert types..."
                        emptyMessage="No alert type found."
                        disabled={loading}
                        showClear={false}
                        ariaLabel={t("notification.alert_type")}
                      />
                    );
                  }}
                />
              )}
              <FormErrorText
                message={errors.alert_type_id?.message}
                className="mt-1"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="alert_type">{t("notification.alert_type")}</Label>
              <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                {readOnlyAlertTypeLabel ?? defaultValues?.alert_type_id ?? "-"}
              </div>
            </div>
          )}
        </FieldGroup>

        <FieldGroup title={t("notification.timezone")}>
          <div className="space-y-2">
            <Label htmlFor="timezone">{t("notification.timezone")} *</Label>
            <Controller
              control={control}
              name="timezone"
              render={({ field }) => (
                <SearchSelect
                  value={field.value}
                  onChange={field.onChange}
                  options={timezoneOptions}
                  placeholder={t("notification.select_timezone")}
                  searchPlaceholder="Search timezones..."
                  emptyMessage="No timezone found."
                  disabled={loading}
                  showClear={false}
                  ariaLabel={t("notification.timezone")}
                />
              )}
            />
            <FormErrorText
              message={errors.timezone?.message}
              className="mt-1"
            />
          </div>
        </FieldGroup>

        <FieldGroup title={t("notification.pin_notification")}>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Controller
                control={control}
                name="is_pinned"
                render={({ field }) => (
                  <Switch
                    id="is_pinned"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={loading}
                  />
                )}
              />
              <Label
                htmlFor="is_pinned"
                className="text-sm text-muted-foreground"
              >
                {isPinned ? t("notification.pinned") : t("notification.unpinned")}
              </Label>
            </div>
            <p className="text-sm text-muted-foreground">{t("notification.pin_hint")}</p>
          </div>
        </FieldGroup>
      </form>
    </div>
  );
}

export default NotificationForm;
